import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  Close as CloseIcon,
  Discount as DiscountIcon
} from '@mui/icons-material';
import { ccyFormat } from '../../helpers/helper';

const SellerSalesModal = ({ 
  open, 
  onClose, 
  seller, 
  sales, 
  loading, 
  dateRange 
}) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'finalized':
        return {
          label: 'Finalizada',
          color: 'success',
          sx: {
            bgcolor: 'success.main',
            color: 'common.white',
            fontWeight: 600,
            '&:hover': { bgcolor: 'success.dark' }
          }
        };
      case 'reserved':
        return {
          label: 'Reservada',
          color: 'warning',
          sx: {
            bgcolor: 'warning.main',
            color: 'common.white',
            fontWeight: 600,
            '&:hover': { bgcolor: 'warning.dark' }
          }
        };
      case 'canceled':
        return {
          label: 'Cancelada',
          color: 'error',
          sx: {
            bgcolor: 'error.main',
            color: 'common.white',
            fontWeight: 600,
            '&:hover': { bgcolor: 'error.dark' }
          }
        };
      case 'returned':
        return {
          label: 'Devuelta',
          color: 'info',
          sx: {
            bgcolor: 'info.main',
            color: 'common.white',
            fontWeight: 600,
            '&:hover': { bgcolor: 'info.dark' }
          }
        };
      default:
        return {
          label: status,
          color: 'default',
          sx: {
            bgcolor: 'grey.500',
            color: 'common.white',
            fontWeight: 600
          }
        };
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={seller?.picture}
              alt={seller?.user_name}
              sx={{ width: 48, height: 48 }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Ventas de {seller?.user_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total de ventas: {sales.length}
                {dateRange?.start_date && dateRange?.end_date && (
                  <> • Período: {new Date(dateRange.start_date).toLocaleDateString('es-AR')} - {new Date(dateRange.end_date).toLocaleDateString('es-AR')}</>
                )}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : sales.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No hay ventas registradas para este vendedor
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Artículo</strong></TableCell>
                  <TableCell><strong>Cliente</strong></TableCell>
                  <TableCell align="center"><strong>Cant.</strong></TableCell>
                  <TableCell align="right"><strong>Precio Unit.</strong></TableCell>
                  <TableCell align="right"><strong>Subtotal</strong></TableCell>
                  <TableCell align="center"><strong>Descuento</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="right"><strong>Pagos</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.map((sale) => {
                  const subtotal = (sale.prices_sold?.sale_price || 0) * (sale.quantity_sold || 0);
                  const hasDiscount = sale.discount_applied;
                  
                  return (
                    <TableRow
                      key={sale._id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {/* Fecha */}
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {sale.created_at ? new Date(sale.created_at).toLocaleDateString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                          }) : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {sale.created_at ? new Date(sale.created_at).toLocaleTimeString('es-AR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </Typography>
                      </TableCell>

                      {/* Artículo */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                            {sale.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {sale.sku}
                          </Typography>
                          {(sale.color_selected || sale.size_selected) && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {sale.color_selected && (
                                <Chip 
                                  label={sale.color_selected} 
                                  size="small" 
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                              {sale.size_selected && (
                                <Chip 
                                  label={sale.size_selected} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Cliente */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {sale.nameSale || 'Sin cliente'}
                        </Typography>
                      </TableCell>

                      {/* Cantidad */}
                      <TableCell align="center">
                        <Chip
                          label={sale.quantity_sold}
                          size="small"
                          color="primary"
                          sx={{ minWidth: 40, fontWeight: 700 }}
                        />
                      </TableCell>

                      {/* Precio Unitario */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          ${ccyFormat(sale.prices_sold?.sale_price || 0)}
                        </Typography>
                        {hasDiscount && sale.original_price && (
                          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            ${ccyFormat(sale.original_price)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Subtotal */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          ${ccyFormat(subtotal)}
                        </Typography>
                      </TableCell>

                      {/* Descuento */}
                      <TableCell align="center">
                        {hasDiscount ? (
                          <Stack alignItems="center" spacing={0.5}>
                            <Chip
                              icon={<DiscountIcon sx={{ fontSize: 14 }} />}
                              label={sale.discount_applied.discount_type === 'percentage' 
                                ? `${sale.discount_applied.discount_percentage}%`
                                : sale.discount_applied.discount_type}
                              size="small"
                              color="error"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Typography variant="caption" color="error.main" fontWeight={600}>
                              -${ccyFormat(sale.discount_applied.discount_amount)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>

                      {/* Estado */}
                      <TableCell align="center">
                        <Chip
                          label={getStatusConfig(sale.status).label}
                          size="small"
                          sx={getStatusConfig(sale.status).sx}
                        />
                        {sale.deposit > 0 && sale.status === 'reserved' && (
                          <Typography variant="caption" display="block" color="warning.main" fontWeight={600}>
                            Seña: ${ccyFormat(sale.deposit)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Métodos de Pago */}
                      <TableCell align="right">
                        {sale.payments && sale.payments.length > 0 ? (
                          <Stack spacing={0.5} alignItems="flex-end">
                            {sale.payments.map((payment, idx) => (
                              <Chip
                                key={idx}
                                label={`${payment.name}: $${ccyFormat(payment.amount)}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={2} width="100%" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Operaciones
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {sales.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Unidades Vendidas
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {sales.reduce((sum, sale) => sum + (sale.quantity_sold || 0), 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ingresos Brutos
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                ${ccyFormat(sales.reduce((sum, sale) => {
                  const subtotal = (sale.prices_sold?.sell_price || 0) * (sale.quantity_sold || 0);
                  return sum + subtotal;
                }, 0))}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Descuentos Aplicados
              </Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">
                -${ccyFormat(sales.reduce((sum, sale) => 
                  sum + (sale.discount_applied?.discount_amount || 0), 0))}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ventas con Oferta
              </Typography>
              <Typography variant="h6" fontWeight={700} color="warning.main">
                {sales.filter(sale => sale.discount_applied).length}
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" onClick={onClose}>
            Cerrar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SellerSalesModal;
